import { useTranslation } from 'react-i18next'
import { isSelfEditable } from './userAttributeFields'
import './UserAttributeFields.scss'

/** Look up an option's display label by value; falls back to the raw value. */
function optionLabel(definition, value) {
  const match = definition.options?.find((o) => o.value === value)
  return match ? match.label : value
}

/** Render a value as static text (for readOnly attributes on the self-service profile). */
function StaticValue({ definition, value, t }) {
  if (definition.type === 'boolean') {
    return value ? t('userAttributeFields.yes') : t('userAttributeFields.no')
  }
  if (definition.type === 'multiselect') {
    if (!Array.isArray(value) || value.length === 0) return t('userAttributeFields.notSet')
    return value.map((v) => optionLabel(definition, v)).join(', ')
  }
  if ((definition.type === 'select' || definition.type === 'radio') && value) {
    return optionLabel(definition, value)
  }
  if (value === '' || value === undefined || value === null) {
    return t('userAttributeFields.notSet')
  }
  return String(value)
}

/** Render the right input control for a definition's type. */
function InputControl({ definition, value, onChange, fieldId, disabled }) {
  const { key, type, options = [] } = definition

  switch (type) {
    case 'textarea':
      return (
        <textarea
          id={fieldId}
          className="user-attribute-fields__textarea"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          rows={3}
          data-testid={`${fieldId}-input`}
        />
      )
    case 'number':
      return (
        <input
          id={fieldId}
          type="number"
          className="user-attribute-fields__input"
          value={value === '' || value === undefined || value === null ? '' : value}
          onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
          disabled={disabled}
          data-testid={`${fieldId}-input`}
        />
      )
    case 'date':
      return (
        <input
          id={fieldId}
          type="date"
          className="user-attribute-fields__input"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          data-testid={`${fieldId}-input`}
        />
      )
    case 'boolean':
      return (
        <input
          id={fieldId}
          type="checkbox"
          className="user-attribute-fields__checkbox"
          checked={!!value}
          onChange={(e) => onChange(e.target.checked)}
          disabled={disabled}
          data-testid={`${fieldId}-input`}
        />
      )
    case 'select':
      return (
        <select
          id={fieldId}
          className="user-attribute-fields__select"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          data-testid={`${fieldId}-input`}
        >
          <option value="" />
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      )
    case 'radio':
      return (
        <div className="user-attribute-fields__radio-group" data-testid={`${fieldId}-input`}>
          {options.map((opt) => (
            <label className="user-attribute-fields__radio-option" key={opt.value}>
              <input
                type="radio"
                name={fieldId}
                value={opt.value}
                checked={value === opt.value}
                onChange={() => onChange(opt.value)}
                disabled={disabled}
              />
              {opt.label}
            </label>
          ))}
        </div>
      )
    case 'multiselect':
      return (
        <select
          id={fieldId}
          multiple
          className="user-attribute-fields__multiselect"
          value={Array.isArray(value) ? value : []}
          onChange={(e) => {
            const selected = Array.from(e.target.selectedOptions).map((o) => o.value)
            onChange(selected)
          }}
          disabled={disabled}
          data-testid={`${fieldId}-input`}
        >
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      )
    case 'text':
    default:
      return (
        <input
          id={fieldId}
          type="text"
          className="user-attribute-fields__input"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          data-testid={`${fieldId}-input`}
        />
      )
  }
}

/**
 * Renders one field per custom attribute definition (#267).
 *
 * - mode="admin": every definition is rendered as an editable input,
 *   regardless of its visibility — admins may see and write everything.
 * - mode="self" (default): only `editable` definitions render as inputs;
 *   everything else (readOnly) renders as static text. `hidden` definitions
 *   never reach a non-admin caller in the first place, so there is nothing
 *   to filter here.
 *
 * Attribute labels and option labels are tenant data and are intentionally
 * not translated.
 */
const UserAttributeFields = ({ definitions = [], values = {}, onChange, mode = 'self', disabled = false }) => {
  const { t } = useTranslation('common')

  if (!definitions.length) return null

  const sorted = [...definitions].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))

  const handleChange = (key, value) => {
    if (typeof onChange === 'function') onChange(key, value)
  }

  return (
    <div className="user-attribute-fields">
      {sorted.map((def) => {
        const editable = mode === 'admin' || isSelfEditable(def)
        const value = values[def.key]
        const fieldId = `user-attribute-${def.key}`

        if (!editable) {
          return (
            <div className="user-attribute-fields__field" key={def.key}>
              <span className="user-attribute-fields__label">{def.label}</span>
              <span className="user-attribute-fields__static-value" data-testid={`${fieldId}-value`}>
                <StaticValue definition={def} value={value} t={t} />
              </span>
            </div>
          )
        }

        return (
          <div className="user-attribute-fields__field" key={def.key}>
            <label className="user-attribute-fields__label" htmlFor={fieldId}>{def.label}</label>
            <InputControl
              definition={def}
              value={value}
              onChange={(v) => handleChange(def.key, v)}
              fieldId={fieldId}
              disabled={disabled}
            />
            {def.helpText && <p className="user-attribute-fields__help">{def.helpText}</p>}
          </div>
        )
      })}
    </div>
  )
}

export default UserAttributeFields
